/**
 * FriendList Component - Displays list of friends with online status
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscribeToFriends, removeFriend } from '../services/friendService';
import { subscribeToMultiplePresences } from '../services/presenceService';
import { inviteFriendToRoom } from '../services/roomService';
import { auth } from '../services/firebase';
import { safeString } from '../utils/safeString';
import '../styles/FriendList.css';

const FriendList = ({ userId }) => {
  const navigate = useNavigate();
  const [friends, setFriends] = useState([]);
  const [presences, setPresences] = useState({});
  const [showConfirm, setShowConfirm] = useState(null);
  const [inviting, setInviting] = useState(null);

  // Subscribe to friends list
  useEffect(() => {
    if (!userId) return;

    const unsubscribe = subscribeToFriends(userId, (updatedFriends) => {
      setFriends(updatedFriends);
    });

    return () => unsubscribe();
  }, [userId]);

  // Subscribe to presence updates for all friends
  useEffect(() => {
    if (friends.length === 0) return;

    const friendIds = friends.map(f => f.userId);
    const unsubscribe = subscribeToMultiplePresences(friendIds, (updatedPresences) => {
      setPresences(updatedPresences);
    });

    return () => unsubscribe();
  }, [friends]);

  const handleRemoveFriend = async (friendId, friendName) => {
    if (showConfirm !== friendId) {
      setShowConfirm(friendId);
      return;
    }

    try {
      await removeFriend(userId, friendId);
      setShowConfirm(null);
    } catch (error) {
      console.error('Error removing friend:', error);
      alert('Failed to remove friend. Please try again.');
    }
  };

  const handleInviteToGame = async (friend) => {
    setInviting(friend.userId);

    try {
      const user = auth.currentUser;
      if (!user) {
        alert('You must be logged in to send invites');
        return;
      }

      // Create room and send invite
      const roomCode = await inviteFriendToRoom(
        user.uid,
        user.displayName,
        user.photoURL,
        friend.userId,
        friend.displayName
      );

      // Navigate to the created room
      navigate(`/room/${roomCode}`);
    } catch (error) {
      console.error('Error inviting friend:', error);
      alert('Failed to send invite. Please try again.');
    } finally {
      setInviting(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="friend-list">
      <div className="friend-list-header">
        <h3>FRIENDS ({friends.length})</h3>
      </div>

      <div className="friend-list-content">
        {friends.length === 0 ? (
          <div className="friend-list-empty">
            <p>No friends yet</p>
            <small>Add friends to play games together!</small>
          </div>
        ) : (
          friends.map((friend) => {
            const presence = presences[friend.userId] || { online: false };
            const isOnline = presence.online;

            return (
              <div key={friend.id} className="friend-item">
                <div className="friend-item-user">
                  <div className="friend-avatar-wrapper">
                    {friend.photoURL && (
                      <img
                        src={safeString(friend.photoURL, '')}
                        alt={safeString(friend.displayName, 'Friend')}
                        className="friend-avatar"
                      />
                    )}
                    <span className={`online-indicator ${isOnline ? 'online' : 'offline'}`}></span>
                  </div>
                  <div className="friend-info">
                    <h4 className="friend-name">
                      {safeString(friend.displayName, 'Friend')}
                      {isOnline && <span className="online-badge">Online</span>}
                    </h4>
                    <p className="friend-since">Friends since {formatDate(friend.since)}</p>
                  </div>
                </div>

                <div className="friend-actions">
                  <button
                    className="friend-action-btn invite-btn"
                    onClick={() => handleInviteToGame(friend)}
                    disabled={inviting === friend.userId}
                    title="Invite to game"
                  >
                    {inviting === friend.userId ? '⏳' : '🎮'} Invite
                  </button>

                  {showConfirm === friend.id ? (
                    <div className="remove-confirm">
                      <button
                        className="friend-action-btn confirm-remove-btn"
                        onClick={() => handleRemoveFriend(friend.id, friend.displayName)}
                      >
                        Confirm
                      </button>
                      <button
                        className="friend-action-btn cancel-remove-btn"
                        onClick={() => setShowConfirm(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className="friend-action-btn remove-btn"
                      onClick={() => handleRemoveFriend(friend.id, friend.displayName)}
                      title="Remove friend"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FriendList;
